/**
 * Free gifts (metaobject `free_gift`, metafield `custom.free_gifts` on products and collections), see README.md "Free gifts".
 *
 * The cart decides which gifts belong in it. snippets/cart-line-items.liquid (cart drawer and cart page) renders a JSON
 * state with the gifts the cart should contain and the gift lines it contains. That snippet is re-rendered with every
 * cart change, so after every `cart:updated` this module compares the two without any request and only talks to the
 * Cart API when something has to be corrected: quantities, missing gifts, gifts whose product is no longer in the cart.
 * The product form already adds the gifts in the same call as the product (product-form-component.js, using the gift
 * lines from this state), so after an add there is normally nothing to correct. This module covers the removal of the
 * product that gave the gift (any path), quantity changes, expired gifts, a second product for the same gift and carts
 * rebuilt by Shopify (browser back after the checkout).
 *
 * Gift lines carry `_free_product: true` (the Discount Kit discount "Free gift" gives 100% discount on those lines) and
 * `_free_gift: <metaobject handle>`. Tier gifts of the cart progress bar have no `_free_gift` property and are left
 * alone here; the progress bar leaves `_free_gift` lines alone.
 */
if (!window.FreeGifts) {
  class FreeGifts {
    // The last state read from the cart markup: { expected: [...], lines: [...] }
    static state = { expected: [], lines: []};

    // Only one reconcile at a time; a cart:updated during a reconcile queues one more run
    static _processing = false;
    static _queued = null;

    static init() {
      // cart-items.js dispatches cart:updated right before it replaces the cart markup: read the state a tick later
      window.addEventListener('cart:updated', event => setTimeout(() => FreeGifts.reconcile(event.detail), 0));
      FreeGifts.reconcile();
    }

    /**
     * The gift lines of the last known cart, for product-form-component.js
     * @returns {Array} [{ key, handle, variantId, quantity }]
     */
    static getGiftLines() {
      return FreeGifts.state.lines;
    }

    /**
     * Reads the state from the freshly rendered cart markup: the sections in the cart:updated detail when present
     * (the response of the cart call that triggered the event), otherwise the page
     * @param {Object} [detail] the cart:updated event detail
     * @returns {Object|null} { expected, lines }, null when there is no cart markup on the page
     */
    static readState(detail) {
      const sectionHtml = Object.values(detail?.sections || {}).find(html => typeof html === 'string' && html.includes('data-free-gifts'));
      const script = sectionHtml
        ? new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('[data-free-gifts]')
        : document.querySelector('[data-free-gifts]');
      if (!script) return null;

      try {
        const state = JSON.parse(script.textContent);
        return { expected: state.expected || [], lines: state.lines || []};
      }
      catch (error) {
        console.error('[free-gifts] invalid state:', error);
        return null;
      }
    }

    /**
     * Expected quantity per gift: the entries of the same gift are added together (per_product) or count once (per_cart),
     * capped at the maximum of the gift
     * @param {Array} entries from snippets/free-gifts.liquid (format json)
     * @returns {Object} handle -> { variantId, quantity }
     */
    static getExpected(entries) {
      const expected = {};
      entries.forEach(entry => {
        const gift = expected[entry.handle] || { variantId: entry.variantId, quantity: 0 };
        gift.quantity = entry.mode === 'per_cart' ? 1 : gift.quantity + (parseInt(entry.lineQuantity, 10) || 0);
        if (entry.maxQuantity > 0) gift.quantity = Math.min(gift.quantity, entry.maxQuantity);
        expected[entry.handle] = gift;
      });
      return expected;
    }

    /**
     * The corrections: quantity changes/removals for existing gift lines and the gift lines to add
     * @param {Array} lines the gift lines in the cart
     * @param {Object} expected from getExpected
     * @returns {{ updates: Object, additions: Array }} updates: line key -> quantity (/cart/update.js), additions: items for /cart/add.js
     */
    static getCorrections(lines, expected) {
      const updates = {};
      const additions = [];
      const linesByHandle = {};

      lines.forEach(line => {
        linesByHandle[line.handle] = linesByHandle[line.handle] || [];
        linesByHandle[line.handle].push(line);
      });

      Object.entries(linesByHandle).forEach(([handle, handleLines]) => {
        const gift = expected[handle];
        // One line keeps the gift (the first one with the right variant), every other line of the same gift is removed
        const keptLine = gift ? handleLines.find(line => String(line.variantId) === String(gift.variantId)) : null;
        handleLines.forEach(line => {
          const quantity = line === keptLine ? gift.quantity : 0;
          if (line.quantity !== quantity) updates[line.key] = quantity;
        });
        if (gift && !keptLine) additions.push(FreeGifts.giftItem(handle, gift));
      });

      Object.entries(expected).forEach(([handle, gift]) => {
        if (!linesByHandle[handle]) additions.push(FreeGifts.giftItem(handle, gift));
      });

      return { updates, additions };
    }

    /**
     * The gift quantity corrections that a quantity change of a cart line causes, for cart-items.js: it sends them in
     * the same /cart/update.js call as the line itself, so the gifts do not need a second round trip afterwards. Only
     * quantity changes of existing gift lines (that is all /cart/update.js can do); a gift that has to be added is
     * still picked up by the reconcile after the cart:updated event.
     * @param {string} key the key of the cart line that changes
     * @param {number} quantity the new quantity of that line (0 removes it, and with it its gifts)
     * @returns {Object} gift line key -> quantity
     */
    static getUpdatesForLineChange(key, quantity) {
      const expectedEntries = FreeGifts.state.expected
        .map(entry => (entry.lineKey === key ? { ...entry, lineQuantity: quantity } : entry))
        .filter(entry => parseInt(entry.lineQuantity, 10) > 0);
      return FreeGifts.getCorrections(FreeGifts.state.lines, FreeGifts.getExpected(expectedEntries)).updates;
    }

    static giftItem(handle, gift) {
      return {
        id: gift.variantId,
        quantity: gift.quantity,
        properties: { _free_product: true, _free_gift: handle },
      };
    }

    static async reconcile(detail = null) {
      if (FreeGifts._processing) {
        FreeGifts._queued = { detail };
        return;
      }
      FreeGifts._processing = true;

      try {
        const state = FreeGifts.readState(detail);
        if (!state) return;
        FreeGifts.state = state;

        const { updates, additions } = FreeGifts.getCorrections(state.lines, FreeGifts.getExpected(state.expected));
        if (!Object.keys(updates).length && !additions.length) return;

        if (Object.keys(updates).length) {
          const response = await fetch(routes.cart_update_url, { ...fetchConfig(), body: JSON.stringify({ updates }) });
          if (!response.ok) throw new Error(response.status);
        }
        if (additions.length) {
          const response = await fetch(routes.cart_add_url, { ...fetchConfig(), body: JSON.stringify({ items: additions }) });
          if (!response.ok) throw new Error(response.status);
        }

        await FreeGifts.renderCart();
      }
      catch (error) {
        console.error('[free-gifts] reconcile error:', error);
      }
      finally {
        FreeGifts._processing = false;
        if (FreeGifts._queued) {
          const { detail: queuedDetail } = FreeGifts._queued;
          FreeGifts._queued = null;
          FreeGifts.reconcile(queuedDetail);
        }
      }
    }

    /**
     * Re-renders the cart (drawer, or the cart page which has no drawer) with the corrected cart and lets the other
     * components know (progress bar, header bubble). The next reconcile that triggers finds nothing to correct.
     */
    static async renderCart() {
      const cartDrawer = document.querySelector('cart-drawer');
      const cartItems = document.querySelector('cart-items');
      const sections = cartDrawer
        ? cartDrawer.getSectionsToRender().map(section => section.section)
        : cartItems?.sectionsToFetch || [];

      const body = JSON.stringify({ sections, sections_url: window.location.pathname });
      const response = await fetch(routes.cart_update_url, { ...fetchConfig(), body });
      if (!response.ok) throw new Error(response.status);
      const cart = await response.json();

      if (cartDrawer) cartDrawer.renderContents(cart);
      else if (cartItems) cartItems.updateContent(cart, null, null, true);

      // The last gift leaves with the last product: the cart is empty now
      const isEmpty = cart.item_count === 0;
      document.querySelectorAll('cart-items, #cartDrawer').forEach(element => element.classList.toggle('cart-is-empty', isEmpty));

      window.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: cart }));
    }
  }

  window.FreeGifts = FreeGifts;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => FreeGifts.init());
  }
  else {
    FreeGifts.init();
  }
}
